import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";

// Account approvals tab — lists pending vendor/EC/marketplace sign-ups and the
// reviewed-vendor history below. Everything is a controlled view; parent owns
// data + handlers so admin actions can refresh the whole dashboard in one go.
export default function AccountApprovalsTab({ pending, allVendors, review, openUserDetail }) {
  const openOnKey = (ev, v) => {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      openUserDetail(v);
    }
  };

  return (
    <div className="space-y-4" data-testid="approvals-tab">
      {pending.length === 0 && (
        <div className="bg-white/60 border border-sage/20 rounded-3xl p-10 text-center text-forest/60" data-testid="no-pending-vendors">
          No accounts waiting for approval.
        </div>
      )}
      {pending.map((v) => (
        <div
          key={v.user_id}
          className="bg-white/80 border border-sage/20 rounded-3xl p-6 shadow-sm cursor-pointer hover:border-sage/50 hover:shadow-md transition"
          data-testid={`pending-vendor-${v.user_id}`}
          onClick={() => openUserDetail(v)}
          role="button"
          tabIndex={0}
          onKeyDown={(ev) => openOnKey(ev, v)}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-heading font-bold text-lg text-forest">{v.profile?.business_name || v.name}</h3>
                <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-sage-light text-sage-dark">{v.role_label || v.role}</span>
                {v.bundle_member && (
                  <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-amber-100 text-amber-800" data-testid={`bundle-badge-${v.user_id}`}>Bundle Deal</span>
                )}
              </div>
              <p className="text-sm text-forest/60">{v.name} · {v.email}</p>
              {v.profile?.description && <p className="text-sm text-forest/70 mt-2 max-w-2xl">{v.profile.description}</p>}
              {v.profile?.location && <p className="text-xs text-forest/50 mt-1">{v.profile.location}</p>}
              {v.profile?.has_multiple_branches && (v.profile?.branches || []).length > 0 && (
                <div className="mt-3 max-w-2xl" data-testid={`pending-vendor-branches-${v.user_id}`}>
                  <p className="text-[11px] font-black uppercase tracking-wide text-sage-dark mb-1.5">
                    Branches ({v.profile.branches.length})
                  </p>
                  <div className="space-y-1.5">
                    {v.profile.branches.map((b, i) => {
                      const addr = [b.address, b.city, b.province].filter(Boolean).join(", ");
                      return (
                        <div
                          key={i}
                          className="bg-softstone/60 rounded-xl px-3 py-2 text-xs text-forest/80"
                          data-testid={`pending-vendor-branch-${v.user_id}-${i}`}
                        >
                          {b.name && <span className="font-bold text-forest">{b.name}</span>}
                          {b.name && addr && <span className="text-forest/40"> · </span>}
                          {addr && <span>{addr}</span>}
                          {(b.phone || b.email) && (
                            <div className="text-[11px] text-forest/60 mt-0.5">
                              {b.phone && <span>{b.phone}</span>}
                              {b.phone && b.email && <span className="text-forest/30"> · </span>}
                              {b.email && <span className="break-all">{b.email}</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-sage-dark/70 font-semibold mt-2">Tap card to view full profile →</p>
            </div>
            <div className="flex gap-2 shrink-0" onClick={(ev) => ev.stopPropagation()}>
              <Button size="sm" className="rounded-full bg-sage hover:bg-sage-dark text-white" onClick={() => review(v.user_id, "approve")} data-testid={`approve-${v.user_id}`}>
                <Check className="w-4 h-4 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light" onClick={() => review(v.user_id, "reject")} data-testid={`reject-${v.user_id}`}>
                <X className="w-4 h-4 mr-1" /> Reject
              </Button>
            </div>
          </div>
        </div>
      ))}

      {allVendors.filter((v) => v.vendor_status !== "pending").length > 0 && (
        <>
          <h3 className="font-heading font-bold text-forest mt-8 mb-2">Reviewed vendors</h3>
          {allVendors.filter((v) => v.vendor_status !== "pending").map((v) => (
            <div
              key={v.user_id}
              className="bg-white/60 border border-sage/20 rounded-2xl p-4 flex flex-wrap items-center gap-3 cursor-pointer hover:border-sage/50 hover:bg-white/80 transition"
              data-testid={`reviewed-vendor-${v.user_id}`}
              onClick={() => openUserDetail(v)}
              role="button"
              tabIndex={0}
              onKeyDown={(ev) => openOnKey(ev, v)}
            >
              <span className="font-bold text-forest text-sm">{v.profile?.business_name || v.name}</span>
              <span className="text-xs text-forest/50">{v.email}</span>
              <span className={`ml-auto text-xs font-bold px-3 py-1 rounded-full ${v.vendor_status === "approved" ? "bg-sage-light text-sage-dark" : "bg-terracotta-light text-terracotta-dark"}`}>
                {v.vendor_status}
              </span>
              {v.vendor_status === "rejected" && (
                <Button size="sm" variant="outline" className="rounded-full" onClick={(ev) => { ev.stopPropagation(); review(v.user_id, "approve"); }} data-testid={`reapprove-${v.user_id}`}>Approve</Button>
              )}
              {v.vendor_status === "approved" && (
                <Button size="sm" variant="ghost" className="rounded-full text-terracotta-dark" onClick={(ev) => { ev.stopPropagation(); review(v.user_id, "reject"); }} data-testid={`revoke-${v.user_id}`}>Revoke</Button>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
